import * as React from 'react'
import { classnames } from '@halo/_utils/classnames'
import { Button, PageHeader } from '@pushly/aqe/lib/components'
import { Tabs } from 'antd'
import { useEffect, useState } from 'react'
import PageAnalysisConfigDetailsTab from '@halo/portals/post-auth/page-analysis-configs/page-analysis-config_details.tab'
import PageAnalysisFieldConfigsTab from '@halo/portals/post-auth/page-analysis-configs/page-analysis-config_field-configs.tab'
import PageAnalysisCatalogConfigTab from '@halo/portals/post-auth/page-analysis-configs/page-analysis-config_catalog-config.tab'
import { PageAnalysisConfigsService } from '@halo/services/page-analysis-configs.service'
import { Container } from 'typescript-ioc'
import TestPageAnalysisDrawer from '@halo/components/test-page-analysis-drawer/test-page-analysis-drawer'
import { PageCategoriesService } from '@pushly/aqe/lib/services'
import { IHydratedPageAnalysisConfig } from '@halo/interfaces/hydrated-page-analysis-config'
import { noop } from '@pushly/aqe/lib/utils/noop'
import { IPageCategory } from '@pushly/aqe/lib/interfaces'
import { CaretRightOutlined } from '@ant-design/icons'
import TabbedView from '@halo/components/tabbed-view/tabbed-view'
import { AppService } from '@halo/services/app.service'
import { Crumb, makeCrumbClick } from '@halo/_utils/breadcrumb'

const PAGE_ANALYSIS_CONFIGS_ROUTE = '/page-analysis-configs'

function EditPageAnalysisConfigPage(props: any) {
    const appService = Container.get(AppService)
    const pageCategoriesService: PageCategoriesService = Container.get(PageCategoriesService)
    const pageAnalysisConfigsService: PageAnalysisConfigsService = Container.get(PageAnalysisConfigsService)
    const pageAnalysisConfigId = props.match.params.pageAnalysisConfigId

    const [hydratedPageAnalysisConfig, setHydratedPageAnalysisConfig] = useState<IHydratedPageAnalysisConfig>()
    const [liveCatalogConfigs, setLiveCatalogConfigs] = useState<any[] | null>(null)
    const [pageCategories, setPageCategories] = useState<IPageCategory[]>()
    const [isTesting, setIsTesting] = useState<boolean>(false)
    const [isLoading, setIsLoading] = useState<boolean>(false)

    useEffect(() => {
        const hydrate = async () => {
            setIsLoading(true)

            const [pc, hpac] = await Promise.all([fetchPageCategories(), fetchHydratedPageAnalysisConfig()])

            setPageCategories(pc)
            setHydratedPageAnalysisConfig(hpac)

            setIsLoading(false)
        }

        hydrate()
    }, [pageAnalysisConfigId])

    async function fetchPageCategories() {
        return (await pageCategoriesService.fetchAll({ query: { domainIds: 'all' } })).data
    }

    async function fetchHydratedPageAnalysisConfig(): Promise<IHydratedPageAnalysisConfig> {
        const res = await pageAnalysisConfigsService.fetchById(pageAnalysisConfigId, {
            query: { hydrated: true },
        })
        return res.data as IHydratedPageAnalysisConfig
    }

    const onCrumbClick = makeCrumbClick(appService)
    const crumbs: Crumb[] | undefined = hydratedPageAnalysisConfig
        ? [
              { title: 'Page Analysis Configs', href: PAGE_ANALYSIS_CONFIGS_ROUTE, onClick: onCrumbClick },
              { title: hydratedPageAnalysisConfig.name },
          ]
        : undefined

    return (
        <>
            <div className={classnames('page', 'single-page-analysis-config')}>
                <div className={classnames('page-wrapper')}>
                    <PageHeader
                        title={
                            hydratedPageAnalysisConfig?.name
                                ? `Page Analysis Config: ${hydratedPageAnalysisConfig.name}`
                                : 'Page Analysis Config'
                        }
                        breadcrumb={crumbs}
                        action={
                            <Button
                                type="primary"
                                icon={<CaretRightOutlined />}
                                onClick={() => setIsTesting(true)}
                                disabled={isLoading || !hydratedPageAnalysisConfig}
                            >
                                Test Page Analysis Config
                            </Button>
                        }
                        onTitleSet={noop}
                    />

                    <TabbedView
                        size="small"
                        animated={{ inkBar: false, tabPane: false }}
                        tabPosition="top"
                        defaultActiveKey="details"
                        pathStyle="hash"
                        availablePathKeys={['details', 'field-configs', 'catalog-config']}
                    >
                        <Tabs.TabPane disabled={isLoading} key="details" tab="Details">
                            <PageAnalysisConfigDetailsTab
                                hydratedPageAnalysisConfig={hydratedPageAnalysisConfig}
                                onUpdate={setHydratedPageAnalysisConfig}
                            />
                        </Tabs.TabPane>

                        <Tabs.TabPane disabled={isLoading} key="field-configs" tab="Field Configs">
                            <PageAnalysisFieldConfigsTab
                                hydratedPageAnalysisConfig={hydratedPageAnalysisConfig}
                                onUpdate={setHydratedPageAnalysisConfig}
                            />
                        </Tabs.TabPane>

                        <Tabs.TabPane disabled={isLoading} key="catalog-config" tab="Catalog Config">
                            <PageAnalysisCatalogConfigTab
                                hydratedPageAnalysisConfig={hydratedPageAnalysisConfig}
                                onUpdate={setHydratedPageAnalysisConfig}
                                onLiveConfigsChange={setLiveCatalogConfigs}
                            />
                        </Tabs.TabPane>
                    </TabbedView>
                </div>
            </div>

            <TestPageAnalysisDrawer
                title="Test Page Analysis"
                hydratedPageAnalysis={
                    isTesting && hydratedPageAnalysisConfig
                        ? {
                              ...hydratedPageAnalysisConfig,
                              catalog_item_configs:
                                  liveCatalogConfigs ?? hydratedPageAnalysisConfig.catalog_item_configs,
                          }
                        : undefined
                }
                onClose={() => setIsTesting(false)}
                pageCategories={pageCategories}
            />
        </>
    )
}

export default EditPageAnalysisConfigPage
